/**
 * Scope Intelligence Findings Facade (100% Shared)
 *
 * Loads durable panel findings, resolves actions, and holds ephemeral
 * in-turn SSE finding flash state.
 *
 * @module useFindingsFacade
 * @category Facades/Shared
 */

import { useState, useCallback, useRef } from 'react';
import { useAppSelector } from '../../../store/hooks';
import { selectUser } from '../../../store/slices/appSlice';
import { findingsService } from '../../../api/services/findings.service';
import { projectService } from '../../../api/services/project.service';
import type {
  Finding,
  FindingAction,
  FindingStatus,
  FindingsSummary,
  FindingsVerdict,
  InTurnFindingFlash,
  ResolveFindingResponse,
} from '../../../types/findings.types';
import type {
  FindingEvent,
  FindingStatusChangedEvent,
  VerdictRecomputedEvent,
} from '../../../types/sse-events';

// ==================== Types ====================

export interface FindingsFacade {
  findings: Finding[];
  summary: FindingsSummary | null;
  verdict: FindingsVerdict | null;
  scopeId: string | null;
  inTurnFlash: InTurnFindingFlash[];
  isLoading: boolean;
  isResolving: boolean;
  error: string | null;
  loadPanel: (scopeId: string, includeDismissed?: boolean) => Promise<void>;
  /**
   * Load the startup-wide aggregate — every scope's findings merged — for the
   * Discussion Room, which has no scope of its own. Resolve still acts by
   * findingId, so cross-scope resolution works unchanged.
   */
  loadPanelAggregate: (projectId: string, includeDismissed?: boolean) => Promise<void>;
  refreshPanel: (includeDismissed?: boolean) => Promise<void>;
  resolve: (
    findingId: string,
    action: FindingAction
  ) => Promise<ResolveFindingResponse | null>;
  applyFindingStatusChanged: (event: FindingStatusChangedEvent) => void;
  applyVerdictRecomputed: (event: VerdictRecomputedEvent) => void;
  pushInTurnFinding: (event: FindingEvent) => void;
  clearInTurnFlash: () => void;
  clearPanel: () => void;
}

const emptySummary = (): FindingsSummary => ({
  total: 0,
  unreviewed: 0,
  investigating: 0,
  confirmed: 0,
  reaffirmed: 0,
  corrected: 0,
  dismissed: 0,
});

/**
 * Provides Scope Intelligence panel state and actions.
 */
export function useFindingsFacade(): FindingsFacade {
  const user = useAppSelector(selectUser);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [summary, setSummary] = useState<FindingsSummary | null>(null);
  const [verdict, setVerdict] = useState<FindingsVerdict | null>(null);
  const [scopeId, setScopeId] = useState<string | null>(null);
  const [inTurnFlash, setInTurnFlash] = useState<InTurnFindingFlash[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scopeIdRef = useRef<string | null>(null);
  // Non-null while the panel is in Discussion-Room aggregate mode (holds the
  // project/startup id). Guards stale aggregate loads and routes refreshPanel.
  const aggregateProjectRef = useRef<string | null>(null);
  const includeDismissedRef = useRef(false);

  const mergeSummaries = (parts: FindingsSummary[]): FindingsSummary =>
    parts.reduce<FindingsSummary>((acc, s) => {
      acc.total += s.total ?? 0;
      acc.unreviewed += s.unreviewed ?? 0;
      acc.investigating += s.investigating ?? 0;
      acc.confirmed += s.confirmed ?? 0;
      acc.reaffirmed += s.reaffirmed ?? 0;
      acc.corrected += s.corrected ?? 0;
      acc.dismissed += s.dismissed ?? 0;
      return acc;
    }, emptySummary());

  const loadPanel = useCallback(async (nextScopeId: string, includeDismissed = false) => {
    if (!nextScopeId) {
      setError('Scope ID is required');
      return;
    }

    setIsLoading(true);
    setError(null);
    scopeIdRef.current = nextScopeId;
    aggregateProjectRef.current = null; // leaving aggregate mode (if we were in it)
    includeDismissedRef.current = includeDismissed;
    setScopeId(nextScopeId);

    try {
      const data = await findingsService.getScopeFindings(nextScopeId, includeDismissed);
      if (scopeIdRef.current !== nextScopeId) return;
      setFindings(data.findings ?? []);
      setSummary(data.summary ?? emptySummary());
      setVerdict(data.verdict ?? null);
      setError(null);
    } catch (err) {
      console.error('Failed to load findings panel:', err);
      setError('Failed to load findings');
      setFindings([]);
      setSummary(null);
      setVerdict(null);
    } finally {
      if (scopeIdRef.current === nextScopeId) {
        setIsLoading(false);
      }
    }
  }, []);

  const loadPanelAggregate = useCallback(async (projectId: string, includeDismissed = false) => {
    if (!projectId) {
      setError('Project ID is required');
      return;
    }

    setIsLoading(true);
    setError(null);
    scopeIdRef.current = null; // aggregate spans all scopes — no single scope key
    aggregateProjectRef.current = projectId;
    includeDismissedRef.current = includeDismissed;
    setScopeId(null);

    try {
      const scopes = await projectService.getStartupScopes(projectId);
      if (aggregateProjectRef.current !== projectId) return;

      const results = await Promise.all(
        (scopes ?? []).map((s) =>
          findingsService.getScopeFindings(s.id, includeDismissed).catch(() => null)
        )
      );
      if (aggregateProjectRef.current !== projectId) return;

      const seen = new Set<string>();
      const merged: Finding[] = [];
      const summaries: FindingsSummary[] = [];
      for (const data of results) {
        if (!data) continue;
        for (const f of data.findings ?? []) {
          if (seen.has(f.id)) continue;
          seen.add(f.id);
          merged.push(f);
        }
        if (data.summary) summaries.push(data.summary);
      }

      setFindings(merged);
      setSummary(mergeSummaries(summaries));
      // Aggregate has no single scope-readiness verdict.
      setVerdict(null);
      setError(null);
    } catch (err) {
      console.error('Failed to load aggregate findings panel:', err);
      setError('Failed to load findings');
      setFindings([]);
      setSummary(null);
      setVerdict(null);
    } finally {
      if (aggregateProjectRef.current === projectId) {
        setIsLoading(false);
      }
    }
  }, []);

  const refreshPanel = useCallback(
    async (includeDismissed?: boolean) => {
      const dismissed =
        typeof includeDismissed === 'boolean' ? includeDismissed : includeDismissedRef.current;
      if (aggregateProjectRef.current) {
        await loadPanelAggregate(aggregateProjectRef.current, dismissed);
        return;
      }
      const id = scopeIdRef.current;
      if (!id) return;
      await loadPanel(id, dismissed);
    },
    [loadPanel, loadPanelAggregate]
  );

  const resolve = useCallback(
    async (findingId: string, action: FindingAction): Promise<ResolveFindingResponse | null> => {
      const userId = user?.id;
      if (!userId) {
        setError('User ID is required to resolve a finding');
        return null;
      }

      setIsResolving(true);
      setError(null);

      try {
        const response = await findingsService.resolveFinding(findingId, action, userId);

        setFindings((prev) =>
          prev.map((f) =>
            f.id === findingId
              ? {
                  ...f,
                  status: response.status,
                  resolution_actor: response.resolution_actor,
                  resolved_at: response.resolved_at,
                  // After resolve, actions typically shrink — keep until refetch if unknown
                  applicable_actions:
                    action === 'dismiss'
                      ? []
                      : f.applicable_actions.filter((a) => a !== action),
                }
              : f
          )
        );
        setVerdict(response.verdict);

        return response;
      } catch (err) {
        console.error('Failed to resolve finding:', err);
        setError('Failed to resolve finding');
        return null;
      } finally {
        setIsResolving(false);
      }
    },
    [user?.id]
  );

  const applyFindingStatusChanged = useCallback((event: FindingStatusChangedEvent) => {
    const { finding_id, status, resolution_actor, scope_id } = event.payload;
    if (scopeIdRef.current && scope_id && scope_id !== scopeIdRef.current) return;

    setFindings((prev) =>
      prev.map((f) =>
        f.id === finding_id
          ? {
              ...f,
              status: status as FindingStatus,
              resolution_actor,
              resolved_at:
                status === 'unreviewed' || status === 'investigating' || status === 'active'
                  ? null
                  : f.resolved_at ?? new Date().toISOString(),
            }
          : f
      )
    );
  }, []);

  const applyVerdictRecomputed = useCallback((event: VerdictRecomputedEvent) => {
    const { scope_id, color, status_line, reviewed_count } = event.payload;
    if (scopeIdRef.current && scope_id && scope_id !== scopeIdRef.current) return;

    setVerdict((prev) => ({
      color,
      status_line,
      reviewed_count,
      confirmed_key_insight_count: prev?.confirmed_key_insight_count,
      investigating_contradiction_count: prev?.investigating_contradiction_count,
      untested_assumption_count: prev?.untested_assumption_count,
    }));
  }, []);

  const pushInTurnFinding = useCallback((event: FindingEvent) => {
    const flash: InTurnFindingFlash = {
      id: `flash-${event.type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type: event.type,
      content: event.payload.content,
      severity: event.payload.severity,
      super_agent_id: event.payload.super_agent_id,
      confidence: event.payload.confidence,
      ...(event.type === 'contradiction'
        ? { side_a: event.payload.side_a, side_b: event.payload.side_b }
        : {}),
    };
    setInTurnFlash((prev) => [...prev, flash]);
  }, []);

  const clearInTurnFlash = useCallback(() => {
    setInTurnFlash([]);
  }, []);

  const clearPanel = useCallback(() => {
    scopeIdRef.current = null;
    aggregateProjectRef.current = null;
    setScopeId(null);
    setFindings([]);
    setSummary(null);
    setVerdict(null);
    setInTurnFlash([]);
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    findings,
    summary,
    verdict,
    scopeId,
    inTurnFlash,
    isLoading,
    isResolving,
    error,
    loadPanel,
    loadPanelAggregate,
    refreshPanel,
    resolve,
    applyFindingStatusChanged,
    applyVerdictRecomputed,
    pushInTurnFinding,
    clearInTurnFlash,
    clearPanel,
  };
}
